import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Play, Trophy, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';

type Period = 'last7' | 'last30' | 'last90';

interface Row {
  driverId: string;
  driverName: string | null;
  totalTrips: number;
  totalKm: number;
  compositeScore: number | null;
  scoreConsumption: number | null;
  scoreIdle: number | null;
  scoreDrivingStyle: number | null;
  scoreSchedule: number | null;
  scoreMaintenance: number | null;
  wasteCostSar: number;
  computedAt: string;
}

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

function tierTone(s: number | null): string {
  if (s === null) return 'bg-muted text-muted-foreground';
  if (s >= 90) return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30';
  if (s >= 75) return 'bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30';
  if (s >= 60) return 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30';
  if (s >= 40) return 'bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500/30';
  return 'bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30';
}

function tierLabel(s: number | null): string {
  if (s === null) return 'No data';
  if (s >= 90) return 'Excellent';
  if (s >= 75) return 'Good';
  if (s >= 60) return 'Coaching';
  if (s >= 40) return 'Review';
  return 'Investigate';
}

export default function DriverLeaderboardPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const [period, setPeriod] = useState<Period>('last30');
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get<Row[]>('/api/v1/driver-scoring/drivers/leaderboard?period=' + period + '&limit=200');
      setRows(r.data);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { refresh(); }, [refresh]);

  async function recompute() {
    setRecomputing(true);
    try {
      await apiClient.post('/api/v1/driver-scoring/recompute?period=' + period);
      await refresh();
    } catch (err) {
      window.alert('Recompute failed: ' + extractErrorMessage(err));
    } finally {
      setRecomputing(false);
    }
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button>
          </Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Trophy className="h-4 w-4" /> Driver leaderboard</div>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4 space-y-3">
        <div className="rounded-lg border border-border p-3 flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-muted-foreground me-2">Period</span>
          {(['last7', 'last30', 'last90'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={'px-3 py-1.5 rounded-md text-sm ' + (period === p ? 'bg-foreground text-background' : 'bg-muted hover:bg-muted/70')}
            >
              {p.replace('last', 'Last ') + 'd'}
            </button>
          ))}
          <div className="flex-1" />
          {isAdmin && (
            <Button onClick={recompute} disabled={recomputing} className="gap-2" size="sm">
              {recomputing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Recompute all
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-400 p-3 text-sm inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {error}</div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">
            No scores computed yet. {isAdmin && 'Tap "Recompute all" above to compute scores for all active drivers.'}
          </div>
        ) : (
          <div className="rounded-lg border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-xs text-muted-foreground bg-muted/40 border-b border-border">
                  <tr>
                    <th className="text-left px-3 py-2">Driver</th>
                    <th className="text-right px-3 py-2">Trips</th>
                    <th className="text-right px-3 py-2">km</th>
                    <th className="text-right px-3 py-2">Waste</th>
                    <th className="text-center px-3 py-2">Cons</th>
                    <th className="text-center px-3 py-2">Idle</th>
                    <th className="text-center px-3 py-2">Style</th>
                    <th className="text-center px-3 py-2">Sched</th>
                    <th className="text-center px-3 py-2">Maint</th>
                    <th className="text-center px-3 py-2">Score</th>
                    <th className="text-left px-3 py-2">Tier</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.driverId} className="border-b border-border last:border-b-0 hover:bg-muted/20">
                      <td className="px-3 py-2">
                        <Link to={'/dashboard/drivers/' + r.driverId + '/performance'} className="text-sm font-medium hover:underline">
                          {r.driverName ?? r.driverId.slice(0, 8)}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.totalTrips}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.totalKm.toFixed(0)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.wasteCostSar > 0 ? fmtSar(r.wasteCostSar) : '—'}</td>
                      <td className="px-3 py-2 text-center"><MiniBar score={r.scoreConsumption} /></td>
                      <td className="px-3 py-2 text-center"><MiniBar score={r.scoreIdle} /></td>
                      <td className="px-3 py-2 text-center"><MiniBar score={r.scoreDrivingStyle} /></td>
                      <td className="px-3 py-2 text-center"><MiniBar score={r.scoreSchedule} /></td>
                      <td className="px-3 py-2 text-center"><MiniBar score={r.scoreMaintenance} /></td>
                      <td className="px-3 py-2 text-center font-mono font-medium">{r.compositeScore !== null ? r.compositeScore.toFixed(0) : '—'}</td>
                      <td className="px-3 py-2">
                        <span className={'inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ' + tierTone(r.compositeScore)}>
                          {tierLabel(r.compositeScore)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function MiniBar({ score }: { score: number | null }) {
  if (score === null) return <span className="text-muted-foreground text-xs">—</span>;
  const color =
    score >= 75 ? 'bg-emerald-500' :
    score >= 60 ? 'bg-amber-500' :
    score >= 40 ? 'bg-orange-500' :
                  'bg-rose-500';
  return (
    <div className="inline-flex items-center gap-1.5">
      <div className="h-1.5 w-12 bg-muted rounded overflow-hidden">
        <div className={'h-full ' + color} style={{ width: score + '%' }} />
      </div>
      <span className="text-xs tabular-nums w-7 text-right">{score.toFixed(0)}</span>
    </div>
  );
}
